import AuditTab from "@/components/AuditTab";
import BillingPage from "@/components/BillingPage";
import DatabaseDetailsModal from "@/components/DatabaseDetails";
import DatabaseForm from "@/components/DatabaseForm";
import DatabaseList from "@/components/DatabaseList";
import DatabaseSearch from "@/components/DatabaseSearch";
import ForceDeleteDialog from "@/components/ForceDeleteDialog";
import QuotaUsageTab from "@/components/QuotaUsageTab";
import SupportCasesPage from "@/components/SupportCasesPage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { databaseApi } from "@/lib/api";
import { CreateDatabaseRequest, DatabaseDetails } from "@/types/database";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  BarChart3,
  CheckCircle,
  CreditCard,
  Database,
  HelpCircle,
  LogOut,
  MessageSquare,
  Plus,
  Star,
  Trash2
} from "lucide-react";
import { clearUserContext, useUserContextStore } from "./utils/store";

function App() {
  const { userContext } = useUserContextStore();
  const [databases, setDatabases] = useState<DatabaseDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showLoginFrom, setShowLoginFrom] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    "databases" | "audit" | "billing" | "quota" | "support"
  >("databases");

  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterSize, setFilterSize] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Force delete dialog state
  const [forceDeleteDialog, setForceDeleteDialog] = useState<{
    isOpen: boolean;
    databaseName: string;
    databaseType: string;
  }>({
    isOpen: false,
    databaseName: "",
    databaseType: "",
  });

  // Database details modal state
  const [selectedDatabase, setSelectedDatabase] =
    useState<DatabaseDetails | null>(null);

  const navigate = useNavigate();

  // Load databases on component mount
  useEffect(() => {
    if (!userContext?.email) {
      navigate("/login");
    }
    loadDatabases();
  }, []);

  // Click outside handler for profile dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileDropdown]);

  const loadDatabases = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await databaseApi.getAllDatabases();
      if (response.success && response.data) {
        setDatabases(response.data.databases);

        // Update selected database if it exists
        if (selectedDatabase) {
          const updatedDatabase = response.data.databases.find(
            (db) => db.dbname === selectedDatabase.dbname
          );
          if (updatedDatabase) {
            setSelectedDatabase(updatedDatabase);
          }
        }
      } else {
        setError(response.error || "Failed to load databases");
      }
    } catch (error) {
      setError("Failed to load databases");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateDatabase = async (data: CreateDatabaseRequest) => {
    setIsCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await databaseApi.createDatabase(data);
      if (response.success && response.data) {
        setSuccess(`Database '${data.dbname}' created successfully!`);
        setShowForm(false);
        // Reload databases to show the new one
        await loadDatabases();
      } else {
        setError(response.error || "Failed to create database");
      }
    } catch (err) {
      setError("Failed to create database");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteDatabase = async (dbname: string) => {
    setError(null);
    setSuccess(null);

    try {
      const response = await databaseApi.deleteDatabase(dbname);
      if (response.success) {
        setSuccess(
          response.data?.message || `Database '${dbname}' marked for deletion`
        );
        // Reload databases to update the status
        await loadDatabases();
      } else {
        setError(response.error || "Failed to delete database");
      }
    } catch (err) {
      setError("Failed to delete database");
    }
  };

  const handleUndoDelete = async (dbname: string) => {
    setError(null);
    setSuccess(null);

    try {
      const response = await databaseApi.undoDelete(dbname);
      if (response.success) {
        setSuccess(
          response.data?.message || `Database '${dbname}' deletion cancelled`
        );
        // Reload databases to update the status
        await loadDatabases();
      } else {
        setError(response.error || "Failed to cancel deletion");
      }
    } catch (err) {
      setError("Failed to cancel deletion");
    }
  };

  const handleForceDelete = async (dbname: string) => {
    // Find the database to get its type
    const database = databases.find((db) => db.dbname === dbname);
    if (!database) {
      setError("Database not found");
      return;
    }

    // Open the force delete dialog
    setForceDeleteDialog({
      isOpen: true,
      databaseName: dbname,
      databaseType: database.dbtype,
    });
  };

  const handleForceDeleteConfirm = async () => {
    setError(null);
    setSuccess(null);

    try {
      const response = await databaseApi.forceDelete(
        forceDeleteDialog.databaseName
      );
      if (response.success) {
        setSuccess(
          response.data?.message ||
            `Database '${forceDeleteDialog.databaseName}' has been permanently deleted`
        );
        // Close the dialog first
        setForceDeleteDialog({
          isOpen: false,
          databaseName: "",
          databaseType: "",
        });
        // Small delay to ensure backend processing is complete
        await new Promise((resolve) => setTimeout(resolve, 500));
        // Reload databases to update the status
        await loadDatabases();
      } else {
        setError(response.error || "Failed to force delete database");
      }
    } catch (err) {
      setError("Failed to force delete database");
    }
  };

  const handleViewDetails = (database: DatabaseDetails) => {
    setSelectedDatabase(database);
  };

  const handleCloseDetails = () => {
    setSelectedDatabase(null);
  };

  const handleRefreshDetails = () => {
    loadDatabases();
  };

  const handleUpdateDatabase = (updatedDatabase: DatabaseDetails) => {
    // Update the database in the list
    setDatabases((prevDatabases) =>
      prevDatabases.map((db) =>
        db.dbname === updatedDatabase.dbname ? updatedDatabase : db
      )
    );

    // Update selected database if it's the same one
    if (
      selectedDatabase &&
      selectedDatabase.dbname === updatedDatabase.dbname
    ) {
      setSelectedDatabase(updatedDatabase);
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleLogout = () => {
    clearUserContext();
    navigate("/login");
  };

  const handleBilling = () => {
    setActiveTab("billing");
    setShowProfileDropdown(false);
  };

  // Filter databases based on search and filters
  const filteredDatabases = databases.filter((database) => {
    const matchesSearch = database.dbname
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || database.dbtype === filterType;
    const matchesSize = filterSize === "all" || database.size === filterSize;
    const matchesStatus =
      filterStatus === "all" || database.status === filterStatus;

    return matchesSearch && matchesType && matchesSize && matchesStatus;
  });

  return (
    <>
      {/* Database Creation Form */}
      {showForm && (
        <div className="fixed z-10 w-full h-screen bg-black/60 flex items-center justify-center ">
          <div className="space-y-4">
            <DatabaseForm
              onSubmit={handleCreateDatabase}
              isLoading={isCreating}
              setShowForm={setShowForm}
            />
          </div>
        </div>
      )}
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <img src="/quickdb-logo.png" alt="" className="h-14 w-12" />
              <div className="flex items-center gap-2">
                <div>
                  <h1 className="text-3xl font-bold">
                    <span className="text-black">Quick</span>
                    <span className="text-yellow-600">DB</span>
                    <sup>
                      {" "}
                      <Badge
                        variant="outline"
                        className=" bg-yellow-100 text-yellow-800 border-yellow-200 text-xs font-medium"
                      >
                        <Star className="w-4 h-4 mr-1" />
                        BETA
                      </Badge>
                    </sup>
                  </h1>

                  <p className="text-muted-foreground -mt-2 ml-1">
                    Manage your databases with ease
                  </p>
                </div>
              </div>
            </div>

            {/* User Profile Dropdown */}
            {userContext && (
              <div className="relative" ref={profileDropdownRef}>
                <button
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={() => setShowProfileDropdown((prev) => !prev)}
                >
                  <img
                    src={
                      userContext.picture ||
                      "https://ui-avatars.com/api/?name=" +
                        encodeURIComponent(
                          (userContext.firstName && userContext.lastName
                            ? `${userContext.firstName} ${userContext.lastName}`
                            : userContext.firstName ||
                              userContext.lastName ||
                              userContext.email) || "U"
                        )
                    }
                    alt={
                      (userContext.firstName && userContext.lastName
                        ? `${userContext.firstName} ${userContext.lastName}`
                        : userContext.firstName ||
                          userContext.lastName ||
                          userContext.email) || "User"
                    }
                    className="w-10 h-10 rounded-full border-2 border-gray-200"
                  />
                  <span className="font-medium text-gray-900">
                    {userContext.firstName && userContext.lastName
                      ? `${userContext.firstName} ${userContext.lastName}`
                      : userContext.firstName ||
                        userContext.lastName ||
                        userContext.email}
                  </span>
                </button>

                {/* Profile Dropdown Menu */}
                {showProfileDropdown && (
                  <div className="absolute right-0 top-full mt-1 min-w-[280px] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    {/* Menu Items */}
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setActiveTab("support");
                          setShowProfileDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <HelpCircle className="w-5 h-5 text-gray-500" />
                        <span className="text-gray-700">Help & Support</span>
                      </button>

                      <div className="border-t border-gray-100 my-2"></div>

                      <button
                        onClick={handleBilling}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <CreditCard className="w-5 h-5 text-gray-500" />
                        <span className="text-gray-700">Billing</span>
                      </button>

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-5 h-5 text-red-500" />
                        <span className="text-red-600">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Messages */}
          {(error || success) && (
            <div className="mb-6">
              {error && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertCircle className="h-4 w-4" />
                      <span>{error}</span>
                      <Button variant="ghost" size="sm" onClick={clearMessages}>
                        ×
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {success && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-green-800">
                      <CheckCircle className="h-4 w-4" />
                      <span>{success}</span>
                      <Button variant="ghost" size="sm" onClick={clearMessages}>
                        ×
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          <div className="my-6">
            <div className="border-t border-gray-200" />
          </div>

          {/* Create Database Button and Sync States */}
          {!showForm && (
            <div className="mb-6 flex items-center gap-2">
              <Button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create New Database
              </Button>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b mb-6">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveTab("databases")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "databases"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Databases
                  <span className="bg-muted px-2 py-0.5 rounded-full text-xs">
                    {databases.length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab("quota")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "quota"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Quota & Usage
                </div>
              </button>
              <button
                onClick={() => setActiveTab("audit")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "audit"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  Audit Trail
                </div>
              </button>

              {/* hiding billing tab for now */}
              {/* <button
                onClick={() => setActiveTab("billing")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "billing"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Billing
                </div>
              </button> */}
              <button
                onClick={() => setActiveTab("support")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "support"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Support Cases
                </div>
              </button>
            </div>
          </div>

          {/* Databases Tab */}
          {activeTab === "databases" && (
            <div>
              {/* Search and Filters */}
              <DatabaseSearch
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterType={filterType}
                setFilterType={setFilterType}
                filterSize={filterSize}
                setFilterSize={setFilterSize}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                totalCount={databases.length}
                filteredCount={filteredDatabases.length}
              />

              <div className="mb-6" />

              <DatabaseList
                databases={filteredDatabases}
                onViewDetails={handleViewDetails}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Quota & Usage Tab */}
          {activeTab === "quota" && (
            <QuotaUsageTab
              isActive={activeTab === "quota"}
              databases={databases}
            />
          )}

          {/* Audit Tab */}
          {activeTab === "audit" && (
            <AuditTab isActive={activeTab === "audit"} />
          )}

          {/* Billing Tab */}
          {activeTab === "billing" && <BillingPage />}

          {/* Support Cases Tab */}
          {activeTab === "support" && (
            <SupportCasesPage isActive={activeTab === "support"} />
          )}
        </div>

        {/* Force Delete Dialog */}
        <ForceDeleteDialog
          isOpen={forceDeleteDialog.isOpen}
          onClose={() =>
            setForceDeleteDialog({
              isOpen: false,
              databaseName: "",
              databaseType: "",
            })
          }
          onConfirm={handleForceDeleteConfirm}
          databaseName={forceDeleteDialog.databaseName}
          databaseType={forceDeleteDialog.databaseType}
        />

        {/* Database Details Modal */}
        {selectedDatabase && (
          <DatabaseDetailsModal
            database={selectedDatabase}
            onClose={handleCloseDetails}
            onRefresh={handleRefreshDetails}
            onUpdateDatabase={handleUpdateDatabase}
            onDelete={handleDeleteDatabase}
            onUndoDelete={handleUndoDelete}
            onForceDelete={handleForceDelete}
          />
        )}
      </div>
    </>
  );
}

export default App;
